import Konva from 'konva/konva'
import EventClusterBadge from './event-cluster-badge'
import i18n from '../../../i18n'

const pointer = { width: 5, height: 5 }
const size = { width: 130, height: 90 }
const titleHeight = 18
const padding = 3
const titleFont = { family: 'Roboto', size: 12, color: 'rgba(0,0,0,0.87)' }
const textFont = { family: 'Roboto', size: 12, color: 'rgba(0,0,0,0.87)' }
const color = 'White'
const border = { color: 'Black', width: 1 }
const verticalLine = { color: 'Grey', width: 1 }
const connector = 10

export default class EventCluster extends Konva.Group {
  constructor ({ yFrom, yTo, clusterId, eventId, events, cycleEvent, setFixedTime }) {
    super()
    Object.assign(this, { yFrom, yTo, clusterId, eventId, events, cycleEvent, setFixedTime })

    this.on('mousemove', this._handleMouseOver.bind(this))
    this.on('mouseout', this._handleMouseOut.bind(this))
  }

  static eventsOverlap (e1x, e2x) {
    return Math.abs(e2x - e1x) < size.width
  }

  get event () {
    return this.events[this.eventId]
  }

  render () {
    const { yFrom } = this
    const yBase = yFrom
    this._renderBox(yBase)
    this._renderTitle(yBase)
    this._renderTitleSeparator(yBase)
    this._renderText(yBase)
    this._renderClusterBadge(yBase)
    this._renderConnector()
    this._renderVerticalLine()
  }

  _renderBox (yBase) {
    const { x } = this.events[0]
    const points = [
      x - size.width / 2, yBase,
      x - size.width / 2, yBase + size.height,
      x - pointer.width / 2, yBase + size.height,
      x, yBase + size.height + pointer.height,
      x + pointer.width / 2, yBase + size.height,
      x + size.width / 2, yBase + size.height,
      x + size.width / 2, yBase
    ]
    this.add(new Konva.Line({
      points,
      closed: true,
      stroke: border.color,
      strokeWidth: border.width,
      fill: color,
      lineJoin: 'round'
    }))
  }

  _renderTitle (yBase) {
    const { x } = this.events[0]
    this.add(new Konva.Text({
      x: x - size.width / 2,
      y: yBase,
      text: this._formatTitle(),
      fontFamily: titleFont.family,
      fontSize: titleFont.size,
      padding,
      fill: titleFont.color,
      width: size.width,
      height: size.height,
      wrap: 'none'
    }))
  }

  _renderTitleSeparator (yBase) {
    const { x } = this.events[0]
    this.add(new Konva.Line({
      points: [x - size.width / 2, yBase + titleHeight, x + size.width / 2, yBase + titleHeight],
      stroke: border.color,
      strokeWidth: border.width,
      fill: color
    }))
  }

  _renderText (yBase) {
    const { x } = this.events[0]
    this.add(new Konva.Text({
      x: x - size.width / 2,
      y: yBase + titleHeight + border.width,
      text: this._formatText(),
      fontFamily: textFont.family,
      fontSize: textFont.size,
      padding,
      fill: textFont.color,
      width: size.width,
      height: size.height - titleHeight - border.width,
      wrap: 'none'
    }))
  }

  _renderClusterBadge (yBase) {
    const { events, eventId } = this
    if (events.length > 1) {
      const { x } = this.events[0]
      const badge = new EventClusterBadge({
        x: x + size.width / 2,
        y: yBase + size.height,
        count: events.length,
        eventId
      })
      this.add(badge)

      badge.on('click touchstart', this._cycleEvent.bind(this))
    }
  }

  _renderConnector () {
    const { yFrom, events } = this
    const { x } = this.event

    // angled line connecting pointer tip of a cycled event to its vertical line
    this.add(new Konva.Line({
      points: [
        events[0].x, yFrom + pointer.height + size.height,
        x, yFrom + pointer.height + size.height + connector
      ],
      stroke: verticalLine.color,
      strokeWidth: verticalLine.width
    }))
  }

  _renderVerticalLine () {
    const { yFrom, yTo } = this
    const { x } = this.event

    this.add(new Konva.Line({
      points: [x, yFrom + pointer.height + size.height + connector, x, yTo],
      stroke: verticalLine.color,
      strokeWidth: verticalLine.width
    }))
  }

  _formatTitle () {
    const { assetName, variableName } = this.event
    return assetName + ' / ' + i18n.t(`event.${variableName}`)
  }

  _formatText () {
    const { values } = this.event
    return values.map(([label, value]) => {
      const eventLabel = i18n.t(`event.${label}`)
      const eventValue = value === null ? '‒' : i18n.t(`${value}`)
      return `${eventLabel}: ${eventValue}`
    }).join('\n')
  }

  _cycleEvent () {
    const { clusterId, events, eventId, cycleEvent } = this
    const newEventId = (eventId + 1) % events.length
    cycleEvent(clusterId, newEventId)

    this.setFixedTime(this.events[newEventId].time)
  }

  _handleMouseOver () {
    this.setFixedTime(this.event.time)
  }

  _handleMouseOut () {
    this.setFixedTime(null)
  }
}
